import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { AppShell } from '@/components/layout/AppShell';
import { RequireAnonymous, RequireAuth, RequirePermission } from '@/routing/guards';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForbiddenPage, NotFoundPage } from '@/components/feedback/StatusPages';

// Eager: the login and dashboard pages are on the critical path for every
// session, so code-splitting them just adds a spinner to the most common route.
import { DashboardPage } from '@/features/dashboard/DashboardPage';

// Lazy: everything reached less often. Keeps the initial bundle to what a user
// needs to sign in and see the dashboard.
const ContactsListPage = lazy(() =>
  import('@/features/contacts/ContactsListPage').then((m) => ({ default: m.ContactsListPage })),
);

const ContactFormPage = lazy(() =>
  import('@/features/contacts/ContactFormPage').then((m) => ({ default: m.ContactFormPage })),
);

const ContactDetailPage = lazy(() =>
  import('@/features/contacts/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage })),
);

const ContactImportPage = lazy(() =>
  import('@/features/contacts/ContactImportPage').then((m) => ({ default: m.ContactImportPage })),
);

const DealsBoardPage = lazy(() => import('@/features/deals/DealsBoardPage'));
const DealDetailPage = lazy(() => import('@/features/deals/DealDetailPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'));
const ProfilePage = lazy(() => import('@/features/auth/ProfilePage'));
const WhatsAppInboxPage = lazy(() => import('@/features/whatsapp/WhatsAppInboxPage'));
const WebAnalyticsPage = lazy(() => import('@/features/analytics/WebAnalyticsPage'));
const CompanyDetailPage = lazy(() => import('@/features/companies/CompanyDetailPage'));
const CampaignsListPage = lazy(() => import('@/features/campaigns/CampaignsListPage'));
const WebsiteLeadsPage = lazy(() => import('@/features/leads/WebsiteLeadsPage'));
const ProspectsInboxPage = lazy(() => import('@/features/prospecting/ProspectsInboxPage'));
const BlogListPage = lazy(() => import('@/features/blog/BlogListPage'));
const BlogEditorPage = lazy(() => import('@/features/blog/BlogEditorPage'));
const AdStudioPage = lazy(() => import('@/features/ads/AdStudioPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const CampaignDetailPage = lazy(() => import('@/features/campaigns/CampaignDetailPage'));
const TasksPage = lazy(() => import('@/features/activities/TasksPage'));
const FollowUpsPage = lazy(() => import('@/features/activities/FollowUpsPage'));

const CompaniesListPage = lazy(() =>
  import('@/features/companies/CompaniesListPage').then((m) => ({ default: m.CompaniesListPage })),
);

const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);

const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

function RouteFallback() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
      <CircularProgress />
    </Box>
  );
}

/** Wraps a lazily-loaded route in its suspense boundary. */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <RequireAnonymous />,
    children: [{ path: '/login', element: <LoginPage /> }],
  },

  {
    element: <RequireAuth />,
    children: [
      // Deliberately OUTSIDE AppShell. An account with mustChangePassword is
      // redirected here from every other route, so showing it navigation to
      // places it cannot reach is only frustrating.
      {
        path: '/change-password',
        element: (
          <Lazy>
            <ChangePasswordPage />
          </Lazy>
        ),
      },

      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          // No permission gate: everyone may see their own account.
          {
            path: '/profile',
            element: (
              <Lazy>
                <ProfilePage />
              </Lazy>
            ),
          },

          {
            element: <RequirePermission permission="dashboard.view" />,
            children: [{ path: '/dashboard', element: <DashboardPage /> }],
          },

          {
            element: <RequirePermission permission="companies.view" />,
            children: [
              {
                path: '/companies/:id',
                element: (
                  <Lazy>
                    <CompanyDetailPage />
                  </Lazy>
                ),
              },
              {
                path: '/companies',
                element: (
                  <Lazy>
                    <CompaniesListPage />
                  </Lazy>
                ),
              },
            ],
          },

          {
            element: <RequirePermission permission="users.view" />,
            children: [
              {
                path: '/users',
                element: (
                  <Lazy>
                    <UsersPage />
                  </Lazy>
                ),
              },
            ],
          },

          {
            element: <RequirePermission permission="settings.view" />,
            children: [
              {
                path: '/settings',
                element: (
                  <Lazy>
                    <SettingsPage />
                  </Lazy>
                ),
              },
            ],
          },

          {
            element: <RequirePermission permission="deals.view" />,
            children: [
              {
                path: '/deals',
                element: (
                  <Lazy>
                    <DealsBoardPage />
                  </Lazy>
                ),
              },
              {
                path: '/deals/:id',
                element: (
                  <Lazy>
                    <DealDetailPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="whatsapp.view" />,
            children: [
              {
                path: '/whatsapp',
                element: (
                  <Lazy>
                    <WhatsAppInboxPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="contacts.view" />,
            children: [
              {
                path: '/website-leads',
                element: (
                  <Lazy>
                    <WebsiteLeadsPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="prospecting.view" />,
            children: [
              {
                path: '/prospects',
                element: (
                  <Lazy>
                    <ProspectsInboxPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="blog.view" />,
            children: [
              { path: '/blog', element: <Lazy><BlogListPage /></Lazy> },
              { path: '/blog/new', element: <Lazy><BlogEditorPage /></Lazy> },
              { path: '/blog/:id', element: <Lazy><BlogEditorPage /></Lazy> },
            ],
          },
          {
            element: <RequirePermission permission="reports.view" />,
            children: [
              {
                path: '/reports',
                element: (
                  <Lazy>
                    <ReportsPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="ads.view" />,
            children: [
              {
                path: '/ads',
                element: (
                  <Lazy>
                    <AdStudioPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="webanalytics.view" />,
            children: [
              {
                path: '/web-analytics',
                element: (
                  <Lazy>
                    <WebAnalyticsPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="campaigns.view" />,
            children: [
              {
                path: '/campaigns',
                element: (
                  <Lazy>
                    <CampaignsListPage />
                  </Lazy>
                ),
              },
              {
                path: '/campaigns/:id',
                element: (
                  <Lazy>
                    <CampaignDetailPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="tasks.view" />,
            children: [
              {
                path: '/tasks',
                element: (
                  <Lazy>
                    <TasksPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="followups.view" />,
            children: [
              {
                path: '/follow-ups',
                element: (
                  <Lazy>
                    <FollowUpsPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: <RequirePermission permission="contacts.view" />,
            children: [
              {
                path: '/contacts',
                element: (
                  <Lazy>
                    <ContactsListPage />
                  </Lazy>
                ),
              },
              {
                path: '/contacts/import',
                element: (
                  <Lazy>
                    <ContactImportPage />
                  </Lazy>
                ),
              },
              {
                path: '/contacts/new',
                element: (
                  <Lazy>
                    <ContactFormPage />
                  </Lazy>
                ),
              },
              {
                path: '/contacts/:id/edit',
                element: (
                  <Lazy>
                    <ContactFormPage />
                  </Lazy>
                ),
              },
              {
                // AFTER /contacts/new and /contacts/import, so those literal
                // segments are not swallowed by the :id parameter.
                path: '/contacts/:id',
                element: (
                  <Lazy>
                    <ContactDetailPage />
                  </Lazy>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  { path: '/403', element: <ForbiddenPage /> },
  { path: '*', element: <NotFoundPage /> },
]);
